import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  GraduationCap,
  Globe2,
  MapPin,
  Search,
  ShieldCheck,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { provinces, scopeLabels } from "../data/provinces";
import type { DatasetMeta, ProvinceStat, Scope, University } from "../types";
import {
  filterUniversities,
  getProvinceId,
  getUniversitiesByProvince,
  searchUniversities
} from "../utils/data";

type MobileInquiryProps = {
  universities: University[];
  meta: DatasetMeta | null;
  provinceStats: ProvinceStat[];
  isLoading: boolean;
};

const mobileScopes: Scope[] = ["all", "undergraduate", "vocational", "private", "military"];
const fujianNeighborProvinceIds = ["CN-FJ", "CN-ZJ", "CN-JX", "CN-GD"];

function formatDate(value?: string) {
  if (!value) return "加载中";
  return value.slice(0, 10);
}

function getMobileResultLimit(query: string, selectedProvince: string | null) {
  if (query.trim()) return 80;
  if (selectedProvince) return 60;
  return 40;
}

function getAdmissionInfoUrl(universityName: string) {
  return `https://gaokao.chsi.com.cn/sch/search.do?searchType=1&yxmc=${encodeURIComponent(universityName)}`;
}

export function MobileInquiry({ universities, meta, provinceStats, isLoading }: MobileInquiryProps) {
  const [query, setQuery] = useState("");
  const [activeScope, setActiveScope] = useState<Scope>("all");
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [isRegionExpanded, setIsRegionExpanded] = useState(false);

  useEffect(() => {
    document.title = "高校查询";
  }, []);

  const scopedUniversities = useMemo(
    () => filterUniversities(universities, activeScope),
    [activeScope, universities]
  );

  const provinceOptions = useMemo(() => {
    return provinceStats.filter((item) => item.count > 0);
  }, [provinceStats]);

  const featuredProvinceOptions = useMemo(() => {
    const statsById = new Map(provinceOptions.map((item) => [item.id, item]));
    return fujianNeighborProvinceIds
      .map((provinceId) => statsById.get(provinceId))
      .filter((item): item is ProvinceStat => Boolean(item));
  }, [provinceOptions]);

  const expandedProvinceOptions = useMemo(
    () => provinceOptions.filter((item) => !fujianNeighborProvinceIds.includes(item.id)),
    [provinceOptions]
  );

  const visibleProvinceOptions = isRegionExpanded
    ? [...featuredProvinceOptions, ...expandedProvinceOptions]
    : featuredProvinceOptions;

  const resultRows = useMemo(() => {
    if (isLoading) return [];
    const rows = selectedProvince
      ? getUniversitiesByProvince(universities, selectedProvince, activeScope)
      : scopedUniversities;
    return searchUniversities(rows, query);
  }, [activeScope, isLoading, query, scopedUniversities, selectedProvince, universities]);

  const hasActiveQuery = Boolean(query.trim() || selectedProvince || activeScope !== "all");
  const displayedRows = hasActiveQuery
    ? resultRows.slice(0, getMobileResultLimit(query, selectedProvince))
    : [];
  const selectedProvinceName = selectedProvince
    ? provinces.find((province) => province.id === selectedProvince)?.name
    : null;
  const contextLabel = selectedProvinceName
    ? `${selectedProvinceName} · ${scopeLabels[activeScope]}`
    : scopeLabels[activeScope];
  const sourceDate = meta?.source_date ?? formatDate(meta?.generated_at);

  return (
    <div className="mobile-shell">
      <main className="mobile-main" aria-busy={isLoading}>
        <header className="mobile-hero" aria-labelledby="mobile-title">
          <div className="mobile-hero-top">
            <div>
              <h1 id="mobile-title">高校查询</h1>
            </div>
            <a className="mobile-full-link" href="/" aria-label="打开完整地图工作台">
              <span>完整地图</span>
              <ArrowUpRight size={15} />
            </a>
          </div>
          <p>
            输入学校、城市、主管部门或标识码，快速核对高校信息并直达官网。
          </p>
          <div className="mobile-metrics" aria-label="数据概览">
            <div>
              <span>记录</span>
              <strong>{isLoading ? "..." : meta?.record_count ?? scopedUniversities.length}</strong>
            </div>
            <div>
              <span>本科</span>
              <strong>{isLoading ? "..." : meta?.undergraduate_count ?? 0}</strong>
            </div>
            <div>
              <span>专科</span>
              <strong>{isLoading ? "..." : meta?.vocational_count ?? 0}</strong>
            </div>
          </div>
        </header>

        <section className="mobile-query-panel" aria-label="高校查询">
          <div className="mobile-search-box">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜学校、城市、标识码"
              aria-label="搜索学校、城市、标识码"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="清空搜索">
                <X size={15} />
              </button>
            )}
          </div>

          <div className="mobile-scope-tabs" aria-label="高校范围筛选">
            {mobileScopes.map((scope) => (
              <button
                key={scope}
                type="button"
                className={activeScope === scope ? "is-active" : ""}
                onClick={() => setActiveScope(scope)}
                aria-pressed={activeScope === scope}
              >
                {scopeLabels[scope]}
              </button>
            ))}
          </div>
        </section>

        <section className="mobile-region-panel" aria-label="按地区查">
          <div className="mobile-section-heading">
            <div>
              <p className="mobile-kicker">按地区查</p>
            </div>
            <div className="mobile-region-actions">
              {selectedProvince && (
                <button type="button" onClick={() => setSelectedProvince(null)}>
                  全部地区
                </button>
              )}
              <button
                type="button"
                className="mobile-region-toggle"
                onClick={() => setIsRegionExpanded((current) => !current)}
                aria-controls="mobile-region-grid"
                aria-expanded={isRegionExpanded}
                aria-label={isRegionExpanded ? "收起全部地区" : "展开全部地区"}
              >
                {isRegionExpanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
              </button>
            </div>
          </div>
          {isLoading ? (
            <p className="mobile-inline-status">地区数据加载中</p>
          ) : (
            <div className="mobile-province-grid" id="mobile-region-grid">
              {visibleProvinceOptions.map((province) => (
                <button
                  key={province.id}
                  type="button"
                  className={selectedProvince === province.id ? "is-active" : ""}
                  onClick={() =>
                    setSelectedProvince((current) => (current === province.id ? null : province.id))
                  }
                  aria-pressed={selectedProvince === province.id}
                >
                  <span>{province.name}</span>
                  <strong>{province.count}</strong>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="mobile-results" aria-labelledby="mobile-results-title">
          <div className="mobile-section-heading">
            <div>
              <p className="mobile-kicker">查询结果</p>
              <h2 id="mobile-results-title">
                {isLoading ? "数据加载中" : hasActiveQuery ? `${resultRows.length} 条匹配` : "等待查询"}
              </h2>
            </div>
            <span>{contextLabel}</span>
          </div>

          {isLoading && (
            <div className="mobile-empty-state">
              <Search size={18} />
              <p>正在加载高校目录，稍后即可查询。</p>
            </div>
          )}

          {!isLoading && !hasActiveQuery && (
            <div className="mobile-empty-state">
              <MapPin size={18} />
              <p>可查询高校目录、所在地、办学层次、主管部门、标识码和官网入口。</p>
            </div>
          )}

          {!isLoading && hasActiveQuery && displayedRows.length === 0 && (
            <div className="mobile-empty-state">
              <Search size={18} />
              <p>没有匹配记录。可以换学校全称、城市名或标识码再试。</p>
            </div>
          )}

          {displayedRows.length > 0 && (
            <div className="mobile-card-list">
              {displayedRows.map((row) => {
                const provinceName = provinces.find((province) => province.id === getProvinceId(row))?.name;
                const admissionInfoUrl = getAdmissionInfoUrl(row.name);
                return (
                  <article className="mobile-school-card" key={`${row.num}-${row.name}`}>
                    <div className="mobile-school-head">
                      <h3>{row.name}</h3>
                      <span className={row.level === "本科" ? "mobile-badge is-blue" : "mobile-badge is-green"}>
                        {row.level || "未标注"}
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>所在地</dt>
                        <dd>{row.location || provinceName || "-"}</dd>
                      </div>
                      <div>
                        <dt>主管部门</dt>
                        <dd>{row.department || "-"}</dd>
                      </div>
                      <div>
                        <dt>标识码</dt>
                        <dd className="mono">{row.code || "-"}</dd>
                      </div>
                      <div>
                        <dt>备注</dt>
                        <dd>{row.remark || "普通"}</dd>
                      </div>
                    </dl>
                    <div className="mobile-school-actions" aria-label={`${row.name} 外部链接`}>
                      {row.official_url && (
                        <a
                          className="is-primary"
                          href={row.official_url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`打开${row.name}官网`}
                        >
                          <Globe2 size={15} />
                          <span>官网</span>
                        </a>
                      )}
                      <a
                        className="is-secondary"
                        href={admissionInfoUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`在阳光高考查询${row.name}招生信息`}
                      >
                        <GraduationCap size={15} />
                        <span>招生信息</span>
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {hasActiveQuery && resultRows.length > displayedRows.length && (
            <p className="mobile-result-note">
              已显示前 {displayedRows.length} 条，继续输入学校名或城市名可缩小范围。
            </p>
          )}
        </section>

        <footer className="mobile-source">
          <ShieldCheck size={16} />
          <p>
            数据来源：{meta?.source_title ?? "教育部高校名单"}；来源日期 {sourceDate}。
          </p>
        </footer>
      </main>
    </div>
  );
}
